import { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { Search, X, Package, Loader2 } from 'lucide-react';
import { inventoryAPI } from '@shared/apiClient';

export default function InventorySearch({ onSelect, placeholder = "Search by item #, MPN, or description..." }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // Debounce search
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch inventory items
  const { data, isLoading } = useQuery(
    ['inventory-search', debouncedSearchTerm],
    () => inventoryAPI.getInventory({ 
      search: debouncedSearchTerm, 
      isActive: true,
      limit: 20 
    }),
    { 
      enabled: debouncedSearchTerm.length >= 2 && isOpen,
      staleTime: 30000 // Cache for 30 seconds
    }
  );

  const inventoryItems = data?.data?.inventory || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (item) => {
    setSelectedItem(item);
    setSearchTerm(item.itemNumber);
    setIsOpen(false);
    
    // Call the onSelect callback with the inventory item data
    if (onSelect) {
      onSelect({
        itemNumber: item.itemNumber,
        description: item.description,
        name: item.itemNumber, // For backward compatibility
        cost: parseFloat(item.cost) || 0,
        price: parseFloat(item.price) || 0,
        quantity: 1 // Default quantity
      });
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setSelectedItem(null);
    setIsOpen(false);
  };

  const handleFocus = () => {
    if (searchTerm.length >= 2) {
      setIsOpen(true);
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedItem(null);
    
    if (value.length >= 2) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <div className="relative" ref={searchRef}>
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        {searchTerm && (
          <button
            onClick={handleClear}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {isLoading ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Searching...</span>
            </div>
          ) : inventoryItems.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 text-center">
              {debouncedSearchTerm.length < 2 
                ? 'Type at least 2 characters to search'
                : 'No parts found'}
            </div>
          ) : (
            <ul className="py-1">
              {inventoryItems.map((item) => (
                <li
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <Package className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {item.itemNumber}
                        </span>
                        {item.manufacturerPartNumber && (
                          <span className="ml-2 text-xs text-gray-400 truncate">
                            MPN: {item.manufacturerPartNumber}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 truncate ml-6">
                        {item.description}
                      </div>
                    </div>
                    <div className="ml-4 flex-shrink-0 text-right">
                      <div className="text-sm font-medium text-gray-900">
                        ${parseFloat(item.cost).toFixed(2)}
                      </div>
                      {item.quantityOnHand !== undefined && (
                        <div className="text-xs text-gray-500">
                          Qty: {parseFloat(item.quantityOnHand).toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

